import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { media } from '@atlaskit/primitives';

import { Adg4Button } from '@/components/adg4/button/src/button';
import { Picture } from '@/components/wpl/image/src/image';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

import { getIsGwp10027NonprofitPricingPage } from '../feature-gates.ts';

const heroContainerCss = css({
	display: 'flex',
	justifyContent: 'center',
	overflow: 'hidden',
	width: '100%',
	[media.only.md]: {
		backgroundImage:
			'url("https://dam-cdn.atl.orangelogic.com/AssetLink/1brb6a15x6gu8788adp68w84y350fn67.webp") , url("https://dam-cdn.atl.orangelogic.com/AssetLink/13nnj4n60686t0re2u4e3xi8r0v5x4us.webp")',
		backgroundPosition: 'left top , right -75px bottom',
		backgroundRepeat: 'no-repeat, no-repeat',
		backgroundSize: '322px auto , auto 345px',
	},
	[media.above.lg]: {
		backgroundImage:
			"url('https://dam-cdn.atl.orangelogic.com/AssetLink/b354j3o63g45l14p758ca34blhscys1w.webp'), url('https://dam-cdn.atl.orangelogic.com/AssetLink/wh4tid444khs6hf56xr05s64052n7w00.webp')",
		backgroundPosition: 'left center, right bottom',
		backgroundRepeat: 'no-repeat, no-repeat',
		backgroundSize: 'auto 100%, 432px 492px',
	},
});
const contentBoxCss = css({
	color: wacToken('color.primary.black'),
	textAlign: 'center',
	position: 'relative',
	paddingBlockStart: '32px',
	maxWidth: '1200px',

	[media.above.sm]: {
		paddingBlockStart: '96px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['a']: {
			marginBlockEnd: '0',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['a']: {
		borderRadius: '4px',
		marginBlockEnd: '28px',
	},
});

const preheaderCss = css({
	fontSize: '18px',
	lineHeight: '26px',
	marginBlockStart: '0',
	marginBlockEnd: '11px',
	fontWeight: '700',
	[media.above.sm]: {
		fontSize: '20px',
		lineHeight: '28px',
		marginBlockEnd: '24px',
	},
});

const titleCss = css({
	fontSize: '60px',
	lineHeight: '70px',
	fontWeight: '400',
	marginBlockStart: '0',
	marginBlockEnd: '18px',
	[media.above.sm]: {
		fontSize: '70px',
		lineHeight: '80px',
		maxWidth: '600px',
		fontWeight: '400',
	},
});

const subtitleCss = css({
	fontSize: '20px',
	lineHeight: '28px',
	maxWidth: '280px',
	fontWeight: '300',
	margin: '0 auto 11px',
	[media.above.sm]: {
		fontSize: '28px',
		lineHeight: '40px',
		maxWidth: '400px',
		margin: '0 auto 15px',
	},
});

const mobileImageCss = css({
	position: 'relative',
	width: '375px',
	height: '281px',
	'@media (min-width: 376px)': {
		display: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['img']: {
		width: '100%',
		height: '100%',
	},
});

export const Hero = () => {
	const ctaButtonUrl = '/software/views/community-license-request#/';

	return (
		<div css={heroContainerCss}>
			<div css={contentBoxCss}>
				<h5 css={preheaderCss}>
					<FormattedMessage
						id="NonprofitPage.hero.preheader"
						defaultMessage="ATLASSIAN FOR NONPROFITS"
					/>
				</h5>
				<h1 css={titleCss}>
					<FormattedMessage
						id="NonprofitPage.hero.title"
						defaultMessage="Pricing that fits your nonprofit’s budget"
					/>
				</h1>
				<h3 css={subtitleCss}>
					{getIsGwp10027NonprofitPricingPage() ? (
						<FormattedMessage
							id="NonprofitPage.hero.subtitle.gwp10027"
							defaultMessage="Get free and discounted Atlassian apps for nonprofit and social enterprise teams"
						/>
					) : (
						<FormattedMessage
							id="NonprofitPage.hero.subtitle.gwp7869"
							defaultMessage="Get discounted Atlassian apps for nonprofit and social enterprise teams"
						/>
					)}
				</h3>
				<Adg4Button
					variant="primary"
					size="small"
					color="blue"
					href={useLocalizedHref(ctaButtonUrl)}
				>
					<FormattedMessage id="NonprofitPage.hero.button" defaultMessage="Apply now" />
				</Adg4Button>
				<div css={mobileImageCss}>
					<Picture
						image={{
							src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/8p5v165n7f507j620h6738eoudo17oae.svg',
							alt: '',
						}}
					/>
				</div>
			</div>
		</div>
	);
};
